import { useState } from "react";
import { FiAlertCircle, FiFilter, FiTrendingDown, FiTrendingUp, FiX } from "react-icons/fi";
import { MdAssignment } from "react-icons/md";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import CartaoEstatistica from "../../../components/relatorios/CartaoEstatistica";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const COLUNAS_ATIVIDADES = [
  { key: "data", label: "DATA" },
  { key: "atividade", label: "ATIVIDADE" },
  { key: "talhao", label: "TALHÃO" },
  { key: "recursos", label: "RECURSOS UTILIZADOS" },
  { key: "tempo_gasto", label: "TEMPO GASTO" },
  { key: "custo_insumos", label: "CUSTO INSUMOS" },
  { key: "custo_maquina", label: "CUSTO MÁQUINA" },
  { key: "custo_mao_obra", label: "CUSTO MÃO DE OBRA" },
  { key: "custo_total", label: "CUSTO TOTAL" },
];

const COLUNAS_CONTAS = [
  { key: "data", label: "DATA" },
  { key: "descricao", label: "DESCRIÇÃO" },
  { key: "valor", label: "VALOR" },
  { key: "categoria", label: "CATEGORIA" },
  { key: "centro_custo", label: "CENTRO CUSTO" },
  { key: "status", label: "STATUS" },
];

const RESUMO_VAZIO = {
  total_custo_atividades: 0,
  total_contas_pagar: 0,
  total_contas_receber: 0,
  resultado_periodo: 0,
};

const RelatorioAtividadesFinanceiro = () => {
  const [dados, setDados] = useState({ atividades: [], contas_pagar: [], contas_receber: [], resumo: RESUMO_VAZIO });
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [abaAtiva, setAbaAtiva] = useState("resumo");

  const [filtros, setFiltros] = useState({ data_inicio: "", data_fim: "" });

  const handleChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");
    try {
      const params = {};
      if (filtros.data_inicio) params.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) params.data_fim = filtros.data_fim;

      const response = await api.get("/relatorios/financeiro/atividades-financeiro/", { params });
      setDados({
        atividades: response.data.atividades || [],
        contas_pagar: response.data.contas_pagar || [],
        contas_receber: response.data.contas_receber || [],
        resumo: response.data.resumo || RESUMO_VAZIO,
      });
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => setFiltros({ data_inicio: "", data_fim: "" });

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "-";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor);
  };

  const exportarExcel = () => {
    if (!dados.atividades.length && !dados.contas_pagar.length && !dados.contas_receber.length) {
      alert("Não há dados para exportar!");
      return;
    }

    const workbook = XLSX.utils.book_new();
    if (dados.atividades.length > 0) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dados.atividades), "Atividades");
    }
    if (dados.contas_pagar.length > 0) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dados.contas_pagar), "Contas a Pagar");
    }
    if (dados.contas_receber.length > 0) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dados.contas_receber), "Contas a Receber");
    }
    XLSX.writeFile(workbook, "relatorio_atividades_financeiro.xlsx");
  };

  // Linha de TOTAL ao final de cada tabela — mesmo padrão de CustoAtiv.jsx/
  // CustoHectare.jsx. As três seções têm colunas diferentes, então cada uma
  // ganha sua própria linha e vai como uma seção separada no PDF (o
  // utilitário compartilhado não mistura tabelas com formatos distintos).
  const linhaTotalAtividades = () => {
    const totais = dados.atividades.reduce(
      (acc, item) => ({
        custo_insumos: acc.custo_insumos + parseFloat(item.custo_insumos || 0),
        custo_maquina: acc.custo_maquina + parseFloat(item.custo_maquina || 0),
        custo_mao_obra: acc.custo_mao_obra + parseFloat(item.custo_mao_obra || 0),
        custo_total: acc.custo_total + parseFloat(item.custo_total || 0),
      }),
      { custo_insumos: 0, custo_maquina: 0, custo_mao_obra: 0, custo_total: 0 }
    );
    return {
      data: "TOTAL", atividade: "", talhao: "", recursos: "", tempo_gasto: "",
      ...totais,
    };
  };

  const linhaTotalContas = (lista) => {
    const total = lista.reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);
    return { data: "TOTAL", descricao: "", valor: total, categoria: "", centro_custo: "", status: "" };
  };

  const formatarValorAtividades = (valor, coluna) =>
    coluna.key.startsWith("custo_") ? formatarMoeda(valor) : (valor ?? "-");

  const formatarValorContas = (valor, coluna) => (coluna.key === "valor" ? formatarMoeda(valor) : (valor ?? "-"));

  const exportarPDF = async () => {
    if (!dados.atividades.length && !dados.contas_pagar.length && !dados.contas_receber.length) {
      alert("⚠️ Não há dados para exportar!");
      return;
    }

    const secoes = [];
    if (dados.atividades.length > 0) {
      secoes.push({
        titulo: "Atividades",
        colunas: COLUNAS_ATIVIDADES,
        dados: [...dados.atividades, linhaTotalAtividades()],
        formatarValor: formatarValorAtividades,
      });
    }
    if (dados.contas_pagar.length > 0) {
      secoes.push({
        titulo: "Contas a Pagar",
        colunas: COLUNAS_CONTAS,
        dados: [...dados.contas_pagar, linhaTotalContas(dados.contas_pagar)],
        formatarValor: formatarValorContas,
      });
    }
    if (dados.contas_receber.length > 0) {
      secoes.push({
        titulo: "Contas a Receber",
        colunas: COLUNAS_CONTAS,
        dados: [...dados.contas_receber, linhaTotalContas(dados.contas_receber)],
        formatarValor: formatarValorContas,
      });
    }

    await exportarRelatorioPDF({
      titulo: "Relatório de Atividades e Financeiro",
      secoes,
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
    });
  };

  const imprimir = () => {
    const printWindow = window.open("", "_blank");
    const linhaAtividades = (item) => `
      <tr>
        <td>${item.data || "-"}</td>
        <td>${item.atividade || "-"}</td>
        <td>${item.talhao || "-"}</td>
        <td>${item.tempo_gasto || "-"}</td>
        <td>${formatarMoeda(item.custo_insumos)}</td>
        <td>${formatarMoeda(item.custo_maquina)}</td>
        <td>${formatarMoeda(item.custo_mao_obra)}</td>
        <td><strong>${formatarMoeda(item.custo_total)}</strong></td>
      </tr>`;
    const linhaConta = (item) => `
      <tr>
        <td>${item.data || "-"}</td>
        <td>${item.descricao || "-"}</td>
        <td>${formatarMoeda(item.valor)}</td>
        <td>${item.categoria || "-"}</td>
        <td>${item.centro_custo || "-"}</td>
        <td>${item.status || "-"}</td>
      </tr>`;

    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Atividades e Financeiro</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            h2 { color: #555; font-size: 14px; margin-top: 24px; }
            .periodo { background: #e8f4f8; padding: 10px; border-radius: 5px; margin-bottom: 20px; }
            .resumo { display: flex; gap: 20px; margin-bottom: 20px; flex-wrap: wrap; }
            .resumo-card { background: #f0f0f0; padding: 15px; border-radius: 8px; text-align: center; min-width: 150px; }
            .resumo-card .valor { font-size: 22px; font-weight: bold; }
            .resumo-card .label { font-size: 12px; color: #666; margin-top: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
            @media print { body { margin: 0; padding: 20px; } .resumo-card { break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Relatorio de Atividades e Financeiro</h1>
          <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
          ${filtros.data_inicio || filtros.data_fim ? `
          <div class="periodo">
            <strong>📅 Período:</strong> ${filtros.data_inicio || "início"} a ${filtros.data_fim || "atual"}
          </div>` : ""}

          <div class="resumo">
            <div class="resumo-card"><div class="valor" style="color:#dc3545;">${formatarMoeda(dados.resumo.total_custo_atividades)}</div><div class="label">Custo das Atividades</div></div>
            <div class="resumo-card"><div class="valor" style="color:#dc3545;">${formatarMoeda(dados.resumo.total_contas_pagar)}</div><div class="label">Total a Pagar</div></div>
            <div class="resumo-card"><div class="valor" style="color:#28a745;">${formatarMoeda(dados.resumo.total_contas_receber)}</div><div class="label">Total a Receber</div></div>
            <div class="resumo-card"><div class="valor" style="color:${dados.resumo.resultado_periodo >= 0 ? "#28a745" : "#dc3545"};">${formatarMoeda(dados.resumo.resultado_periodo)}</div><div class="label">Resultado do Período</div></div>
          </div>

          <h2>🌱 Atividades</h2>
          <table>
            <thead><tr><th>DATA</th><th>ATIVIDADE</th><th>TALHÃO</th><th>TEMPO GASTO</th><th>INSUMOS</th><th>MÁQUINA</th><th>MÃO DE OBRA</th><th>TOTAL</th></tr></thead>
            <tbody>${dados.atividades.map(linhaAtividades).join("") || '<tr><td colspan="8">Nenhuma atividade no período</td></tr>'}</tbody>
          </table>

          <h2>🔴 Contas a Pagar</h2>
          <table>
            <thead><tr><th>DATA</th><th>DESCRIÇÃO</th><th>VALOR</th><th>CATEGORIA</th><th>CENTRO CUSTO</th><th>STATUS</th></tr></thead>
            <tbody>${dados.contas_pagar.map(linhaConta).join("") || '<tr><td colspan="6">Nenhuma conta a pagar no período</td></tr>'}</tbody>
          </table>

          <h2>🟢 Contas a Receber</h2>
          <table>
            <thead><tr><th>DATA</th><th>DESCRIÇÃO</th><th>VALOR</th><th>CATEGORIA</th><th>CENTRO CUSTO</th><th>STATUS</th></tr></thead>
            <tbody>${dados.contas_receber.map(linhaConta).join("") || '<tr><td colspan="6">Nenhuma conta a receber no período</td></tr>'}</tbody>
          </table>

          <div class="footer">Sistema de Gestão Agropecuária - Relatório Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={MdAssignment}
            titulo="Relatório de Atividades e Financeiro"
            descricao="Visão consolidada do período: custo das atividades de campo, contas a pagar e a receber"
          />

          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Data Início</label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Data Fim</label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {erro && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <BotaoRelatorio variante="primario" onClick={buscarDados} carregando={carregando} className="flex-1">
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 9999, overflow: "auto" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{ zIndex: 10000, maxHeight: "calc(100vh - 4rem)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <MdAssignment size={22} className="text-brand-green" />
                Relatório de Atividades e Financeiro
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition flex-shrink-0"
              >
                <FiX size={18} className="text-gray-600" />
              </button>
            </div>

            <div className="border-b border-gray-200 px-6">
              <div className="flex gap-4">
                <button
                  onClick={() => setAbaAtiva("resumo")}
                  className={`py-3 px-4 font-medium transition ${abaAtiva === "resumo" ? "border-b-2 border-brand-green text-brand-green" : "text-gray-500 hover:text-gray-700"}`}
                >
                  📊 Resumo
                </button>
                <button
                  onClick={() => setAbaAtiva("atividades")}
                  className={`py-3 px-4 font-medium transition ${abaAtiva === "atividades" ? "border-b-2 border-brand-green text-brand-green" : "text-gray-500 hover:text-gray-700"}`}
                >
                  🌱 Atividades
                </button>
                <button
                  onClick={() => setAbaAtiva("pagar")}
                  className={`py-3 px-4 font-medium transition ${abaAtiva === "pagar" ? "border-b-2 border-red-500 text-red-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  🔴 Contas a Pagar
                </button>
                <button
                  onClick={() => setAbaAtiva("receber")}
                  className={`py-3 px-4 font-medium transition ${abaAtiva === "receber" ? "border-b-2 border-green-500 text-green-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  🟢 Contas a Receber
                </button>
              </div>
            </div>

            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3 flex-shrink-0">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>Exportar para Excel</BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>Exportar para PDF</BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>Imprimir</BotaoRelatorio>
            </div>

            <div className="flex-1 overflow-auto p-6" style={{ minHeight: 0 }}>
              {abaAtiva === "resumo" && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <CartaoEstatistica
                    icone={FiTrendingDown}
                    rotulo="Custo das Atividades"
                    valor={formatarMoeda(dados.resumo.total_custo_atividades)}
                    subvalor={`${dados.atividades.length} atividade(s)`}
                    cor="dourado"
                  />
                  <CartaoEstatistica
                    icone={FiTrendingDown}
                    rotulo="Total a Pagar"
                    valor={formatarMoeda(dados.resumo.total_contas_pagar)}
                    subvalor={`${dados.contas_pagar.length} conta(s)`}
                    cor="vermelho"
                  />
                  <CartaoEstatistica
                    icone={FiTrendingUp}
                    rotulo="Total a Receber"
                    valor={formatarMoeda(dados.resumo.total_contas_receber)}
                    subvalor={`${dados.contas_receber.length} conta(s)`}
                    cor="verde"
                  />
                  <CartaoEstatistica
                    rotulo="Resultado do Período"
                    valor={formatarMoeda(dados.resumo.resultado_periodo)}
                    subvalor="Receber − Pagar − Custo das atividades"
                    cor={dados.resumo.resultado_periodo >= 0 ? "azul" : "vermelho"}
                  />
                </div>
              )}

              {abaAtiva === "atividades" && (
                dados.atividades.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          {COLUNAS_ATIVIDADES.map((c) => (
                            <th key={c.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dados.atividades.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                              <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">{item.atividade || "-"}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.talhao || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.recursos || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.tempo_gasto || "-"}</td>
                            <td className="px-4 py-3 text-sm text-green-600 whitespace-nowrap">{formatarMoeda(item.custo_insumos)}</td>
                            <td className="px-4 py-3 text-sm text-orange-600 whitespace-nowrap">{formatarMoeda(item.custo_maquina)}</td>
                            <td className="px-4 py-3 text-sm text-purple-600 whitespace-nowrap">{formatarMoeda(item.custo_mao_obra)}</td>
                            <td className="px-4 py-3 text-sm text-red-600 font-bold whitespace-nowrap">{formatarMoeda(item.custo_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EstadoVazio titulo="Nenhuma atividade encontrada" descricao="Ajuste o período selecionado e tente novamente." />
                )
              )}

              {abaAtiva === "pagar" && (
                dados.contas_pagar.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          {COLUNAS_CONTAS.map((c) => (
                            <th key={c.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dados.contas_pagar.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.descricao || "-"}</td>
                            <td className="px-4 py-3 text-sm text-red-600 font-medium whitespace-nowrap">{formatarMoeda(item.valor)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.categoria || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.centro_custo || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.status || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EstadoVazio titulo="Nenhuma conta a pagar encontrada" descricao="Ajuste o período selecionado e tente novamente." />
                )
              )}

              {abaAtiva === "receber" && (
                dados.contas_receber.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          {COLUNAS_CONTAS.map((c) => (
                            <th key={c.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dados.contas_receber.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.descricao || "-"}</td>
                            <td className="px-4 py-3 text-sm text-green-600 font-medium whitespace-nowrap">{formatarMoeda(item.valor)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.categoria || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.centro_custo || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.status || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EstadoVazio titulo="Nenhuma conta a receber encontrada" descricao="Ajuste o período selecionado e tente novamente." />
                )
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>Fechar</BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioAtividadesFinanceiro;
